import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import BlockCard from '../Cards/BlockCard'
import ContentfulButtonV3 from '../Utilities/Buttons/V3/ContentfulButtonV3'
import ContentfulForm from '../Utilities/ContentfulForm'
import { CurvedArrow } from '../icons'
import { TypeWriter } from '../Utilities'
import PeopleChoose from '../PeopleChoose'
import RatesTermsRibbon from '../RatesTermsRibbon'
import FeatureV2 from '../FeatureV2'
import PromoCard from '../Cards/PromoCard'
import AlertBannerDecorative from '../../assets/images/decoratives/alert-banner-decoration.inline.svg'
import HardCoded from '../HardCoded'
import CustomHeroDisclaimer from './CustomHeroDisclaimer'

function CustomHeroV3({ data, slug, carouselIndex }) {
  const CONTENT_TYPE_MP4 = 'video/mp4'
  const CONTENT_TYPE_WEBM = 'video/webm'
  const { bannerAlert } = data
  const bannerDiv =
    slug === 'credit-card' && bannerAlert !== null ? (
      <div className="banner">
        <AlertBannerDecorative className="svg" />
        <div className="banner-message">{bannerAlert}</div>
      </div>
    ) : null

  const {
    headline,
    headlineLogos,
    backgroundMedia,
    backgroundImageAlignment,
    image,
    imageCard,
    theme,
    decoratives,
    buttonColor,
    contentAlign,
    subHeadline,
    hasPromoCodeBox,
    beforeText,
    showButtonArrow,
    afterText,
    buttons,
    cards,
    contents,
    disclaimer,
    size,
    activeNotFoundDesign,
    __typename
  } = data

  const [formImage, setformImage] = useState('')

  // process background
  const [bgMp4s, bgWebms, bgImgs] = [[], [], []]
  if (backgroundMedia) {
    backgroundMedia.forEach(el => {
      if (el.file?.contentType === CONTENT_TYPE_MP4) {
        bgMp4s.push(el)
      } else if (el.file?.contentType === CONTENT_TYPE_WEBM) {
        bgWebms.push(el)
      } else {
        bgImgs.push(el)
      }
    })
  }

  if (bgMp4s.length) bgMp4s[0] = bgMp4s[Math.floor(Math.random() * bgMp4s.length)]
  if (bgWebms.length) bgWebms[0] = bgWebms[Math.floor(Math.random() * bgWebms.length)]
  if (bgImgs.length) bgImgs[0] = bgImgs[Math.floor(Math.random() * bgImgs.length)]

  const mp4Comp = bgMp4s[0] ? <source srcSet={bgMp4s[0].file.url} type={CONTENT_TYPE_MP4} /> : null
  const webmComp = bgWebms[0] ? (
    <source srcSet={bgWebms[0].file.url} type={CONTENT_TYPE_WEBM} />
  ) : null
  const bgImageComp = bgImgs[0] ? (
    <GatsbyImage
      loading="eager"
      className={`background-img ${!image ? 'new-location' : ''}`}
      imgClassName="background-img-inner"
      imgStyle={{ height: '100%' }}
      image={getImage(bgImgs[0].image)}
      alt=""
    />
  ) : null

  let backgroundLarge = null
  const imageComp = !image ? null : (
    <GatsbyImage
      loading="eager"
      className={`custom-hero-img ${formImage} ${contents ? 'content-size' : ''} ${
        activeNotFoundDesign ? `not-found` : ''
      }`}
      imgClassName="custom-hero-img-inner"
      image={getImage(image.image)}
      alt={image.altText}
      objectFit="scale-down"
      objectPosition="right"
    />
  )
  let backgroundMobile = null
  if (mp4Comp || webmComp) {
    backgroundLarge = (
      <div className="bg-video">
        <div className="overlay" />
        <video autoPlay muted loop>
          {mp4Comp}
          {webmComp}
          {bgImageComp}
        </video>
      </div>
    )
    backgroundMobile = !bgImageComp ? null : (
      <div className="bg-image">
        {bgImageComp}
        {imageComp}
      </div>
    )
  } else {
    backgroundMobile =
      bgImageComp || imageComp ? (
        <div
          className={`bg-image ${backgroundImageAlignment} ${
            activeNotFoundDesign ? 'not-found' : ''
          }`}
        >
          {bgImageComp}
          {imageComp}
        </div>
      ) : null
  }

  const imageCardComp = imageCard ? (
    <div className="image-card">
      <h1>{imageCard[0].headline}</h1>
      <ul>
        {imageCard[0].description.map(item => {
          return <li key={item}>{item}</li>
        })}
      </ul>
    </div>
  ) : null

  const backgroundDiamonds =
    decoratives?.indexOf('Diamonds') >= 0 ? <div className="decorative--diamonds" /> : null

  const backgroundCreditCardDecor =
    decoratives?.indexOf('Credit Card Background') >= 0 ? (
      <div className="decorative decorative--credit-card" />
    ) : null

  let headlineComp
  if (headline && headline.length) {
    headlineComp = (
      <h1 className={`headline${activeNotFoundDesign ? ' not-found' : ''}`}>
        {headline
          .replace(/\\u00A0/g, '\u00A0')
          .split(/([[{].+?[\]}])/)

          // TODO: standardize these mapping functions (i.e. in BlockCardGrid, HeadlineBlockCard)
          .map((part, index) => {
            const lastIndex = headline.replace(/\\u00A0/g, '\u00A0').split(/([[{].+?[\]}])/).length
            if (part.substr(-1, 1) === ']' && part.substr(0, 1) === '[') {
              return <TypeWriter key={part} text={part.substr(1, part.length - 2).split('|')} />
            }
            /* eslint-disable react/no-array-index-key */
            if (part === '{mbr}') {
              return <span key={`${part}-${index}`} className="mobile-break" />
            }
            if (part === '{dbr}') return <span key={`${part}-${index}`} className="desktop-break" />

            if (index === lastIndex - 1 && part.indexOf('<br') > -1) {
              const lastLineParts = part.split('<br>')
              return lastLineParts.map(lastLinePart => {
                return (
                  <span key={lastLinePart} className="headline-two">
                    {lastLinePart}
                  </span>
                )
              })
            }
            if (index === lastIndex - 1 && part.indexOf('<br') === -1) {
              return (
                <span key={part} className="headline-two">
                  {part}
                </span>
              )
            }
            return part
          })}
      </h1>
    )
  } else {
    headlineComp = null
  }

  /* eslint-disable react/no-danger */
  let subHeadlineComp

  if (!subHeadline) {
    subHeadlineComp = null
  } else {
    let __html = subHeadline
      .split('{mbr}')
      .join('<span class="mobile-break"></span>')
      .split('{dbr}')
      .join('<span class="desktop-break"></span>')
    const divider = ' | '
    if (__html.indexOf(divider > -1)) {
      __html = `<span>${__html.replaceAll(divider, `</span><span class="vl"></span><span>`)}</span>`
    }
    subHeadlineComp = (
      <p
        className={`subheadline ${activeNotFoundDesign ? 'not-found' : ''}`}
        dangerouslySetInnerHTML={{ __html }}
      />
    )
  }

  const promoBoxComp =
    !hasPromoCodeBox || hasPromoCodeBox === 'None' || !slug ? null : (
      <PromoCard
        promoData={{
          landing_keyword: slug,
          type: hasPromoCodeBox
        }}
      />
    )

  let arrowColor
  const colorTheme = theme && theme.toLowerCase()
  switch (colorTheme) {
    case 'white':
      arrowColor = '#0d5bff'
      break
    case 'gray':
      arrowColor = '#2a2a3f'
      break
    default:
      arrowColor = '#fff'
      break
  }
  const buttonArrowComp = showButtonArrow ? (
    <CurvedArrow
      className="button-arrow"
      fill={arrowColor}
      stroke={arrowColor}
      height={34}
      width={29}
    />
  ) : null

  const beforeTextComp = !beforeText ? null : (
    <div className="before-text">
      {buttonArrowComp}
      <p dangerouslySetInnerHTML={{ __html: beforeText }} />
    </div>
  )
  const afterTextComp = !afterText ? null : (
    <div className="after-text">
      <p dangerouslySetInnerHTML={{ __html: afterText }} />
    </div>
  )

  const doSlot = slotIndex => {
    const content = document.querySelector('div.hero-carousel > div.content')
    if (!content) return

    Array.from(content.classList).forEach(cl => {
      if (cl.indexOf('slot-') === 0) content.classList.remove(cl)
    })

    content.classList.add(`slot-${slotIndex}`)
  }

  /* eslint-disable react/no-array-index-key */
  const buttonComps =
    !buttons || !buttons.length ? null : (
      <div className="buttons">
        {buttons.map((buttonOrForm, index) => {
          const isMainCarouselButton = index === carouselIndex
          const typeName = buttonOrForm?.__typename
          let hoverFunc = null
          if (!isMainCarouselButton && buttonOrForm.style === 'Primary') {
            hoverFunc = () => doSlot(index)
          }

          if (typeName === 'ContentfulHardCoded') {
            useEffect(() => {
              setformImage('form-image')
            }, [])

            return <HardCoded key={buttonOrForm.id} data={buttonOrForm} />
          }

          if (typeName === 'ContentfulForm') {
            return <ContentfulForm data={buttonOrForm} key={index} />
          }

          if (typeName === 'ContentfulButton') {
            return (
              <ContentfulButtonV3
                parentComponent={__typename}
                data={buttonOrForm}
                key={index}
                isMain={isMainCarouselButton}
                onMouseOver={hoverFunc}
              />
            )
          }

          return null
        })}
      </div>
    )

  const cardsComp =
    !cards || !cards.length ? null : (
      <div className={`blockcards ${buttonColor.toLowerCase()}`}>
        {cards.map((card, index) => (
          <BlockCard data={card} key={index} useDivOutterContainer />
        ))}
      </div>
    )

  const contentComps =
    !contents || !contents.length ? null : (
      <div className="contents">
        {contents.map(row => {
          const type = row?.sys?.contentType?.sys?.id
          if (type === 'imageSet') {
            return <FeatureV2 key={row.id} data={row} />
          }
          if (type === 'chooseUsSet') {
            return <PeopleChoose key={row.id} contentfulEntryTitle={row.entryTitle} />
          }
          if (type === 'ratesAndTermsRibbon') {
            return <RatesTermsRibbon key={row.id} data={row} theme={theme} />
          }
          return null
        })}
      </div>
    )

  let sectionClasses = ['custom-hero']
  if (theme && theme !== 'Default (Dark)') {
    sectionClasses.push(theme.toLowerCase())
  }
  sectionClasses = sectionClasses.join(' ')

  let headlineLogosComp = []
  if (headlineLogos && headlineLogos.length) {
    headlineLogos.forEach(logo => {
      headlineLogosComp.push(
        <GatsbyImage image={getImage(logo.image)} className="logo" alt={logo.altText} />
      )
    })
    headlineLogosComp = <div className="logos">{headlineLogosComp}</div>
  }

  const sizeClass = size === 'Small' ? 'small' : ''
  return (
    <section className={sectionClasses}>
      {backgroundLarge}
      {backgroundMobile}
      <div
        className={`container__inner ${contentAlign?.toLowerCase() || ''} ${sizeClass} ${
          activeNotFoundDesign ? 'not-found' : ''
        } ${!image ? 'no-image-spacing' : ''}`}
      >
        {bannerDiv}
        {backgroundDiamonds}
        {backgroundCreditCardDecor}
        <div className={`intro ${activeNotFoundDesign ? 'not-found' : ''}`}>
          {headlineLogosComp}
          {headlineComp}
          {subHeadlineComp}
          {promoBoxComp}
          {buttonComps}
          {beforeTextComp}
          {afterTextComp}
          {imageCardComp}
        </div>
        {cardsComp}
        {contentComps}
        <CustomHeroDisclaimer disclaimer={disclaimer} />
      </div>
      {image ? (
        <div className={`custom-hero-img-div ${activeNotFoundDesign ? 'not-found' : ''}`}>
          {imageComp}
        </div>
      ) : (
        ''
      )}
      {activeNotFoundDesign && (
        <div className="container__inner not-found-mobile">
          <div className="intro">
            {headlineComp}
            {subHeadlineComp} {buttonComps}
          </div>
        </div>
      )}
    </section>
  )
}

CustomHeroV3.defaultProps = {
  carouselIndex: -1,
  slug: null
}

CustomHeroV3.propTypes = {
  carouselIndex: PropTypes.number,
  data: PropTypes.object.isRequired,
  slug: PropTypes.string
}

export default CustomHeroV3
